import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Mail, Phone, MapPin, Clock } from 'lucide-react';
import Footer from '@/components/Footer';
import Navigation from '@/components/Navigation';
import FloatingActionButtons from '@/components/FloatingActionButtons';
import { useCallback, useEffect, useRef } from 'react';

const Kontakt = () => {
  // Använd useRef för att hantera scrollning
  const contactFormRef = useRef<HTMLDivElement>(null);
  
  const scrollToForm = useCallback(() => {
    if (contactFormRef.current) {
      contactFormRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);
  const arrowRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const arrow = arrowRef.current;
    if (!arrow) return;

    const animate = () => {
      arrow.style.transform = 'translateY(0)';
      requestAnimationFrame(() => {
        arrow.style.transition = 'transform 1s ease-in-out';
        arrow.style.transform = 'translateY(-10px)';
        
        setTimeout(() => {
          arrow.style.transition = 'transform 1s ease-in-out';
          arrow.style.transform = 'translateY(0)';
        }, 1000);
      });
    };

    // Initial animation
    const timer = setTimeout(animate, 500);
    
    // Repeat animation every 2 seconds
    const interval = setInterval(animate, 2000);
    
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, []);
  return (
    <div className="relative w-full overflow-x-hidden">
      <Navigation />
      <Helmet>
        <title>Kontakta Oss - RenGlans Städtjänster</title>
        <meta name="description" content="Kontakta RenGlans Städtjänster idag för en kostnadsfri offert eller frågor om våra tjänster. Vi finns här för att hjälpa dig med alla dina städbehov." />
        <meta property="og:title" content="Kontakta Oss - RenGlans Städtjänster" />
        <meta property="og:description" content="Kontakta oss idag för professionell städhjälp. Vi erbjuder kostnadsfria offerter och skräddarsydda lösningar för dina städbehov." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[80vh] pt-32 pb-20 flex items-center justify-center overflow-hidden" style={{ minHeight: 'calc(80vh - 120px)' }}>
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat -z-10"
          style={{
            backgroundImage: 'url(/house-cleaners.webp)',
            filter: 'blur(5px)',
            transform: 'scale(1.02)',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
        >
          <div className="absolute inset-0 bg-[hsl(var(--hero-overlay))]/40" />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10 mt-8 md:mt-12 lg:mt-16">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6 leading-tight text-white"
          >
            Kontakta Oss
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl md:text-2xl max-w-3xl mx-auto mb-8 text-white"
          >
            Använd gärna den blåa chattbubblan längst ner i högra hörnet om du har en fråga!
          </motion.p>
          </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden translate-y-px">
          <svg 
            viewBox="0 0 1200 120" 
            preserveAspectRatio="none"
            className="w-full h-12 md:h-16"
          >
            <path d="M0,60 C200,120 400,0 600,60 C800,120 1000,0 1200,60 V120 H0 V60 Z" className="fill-red-100" />
          </svg>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-red-100 w-full" ref={contactFormRef} id="contact-form">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-center text-red-700 mb-12 md:mb-16 font-['Raleway'] uppercase tracking-wide [text-shadow:1px_1px_2px_rgba(255,255,255,0.8)]">Kontaktuppgifter</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 w-full max-w-[95vw] mx-auto">
              <div className="bg-white p-6 text-center border-r-0 md:border-r border-red-500 rounded-l-lg md:rounded-l-lg md:rounded-r-none">
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Adress</h3>
                <p className="text-gray-700 font-medium text-lg">Storgatan 12<br />123 45 Stockholm</p>
              </div>

              <div className="bg-white p-6 text-center border-r-0 md:border-r border-red-500 rounded-none">
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">E-post</h3>
                <p className="text-gray-700 font-medium text-lg">info@renglans.se</p>
              </div>

              <div className="bg-white p-6 text-center border-r-0 md:border-r border-red-500 rounded-none">
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Telefon</h3>
                <p className="text-gray-700 font-medium text-lg">010-123 45 67</p>
              </div>

              <div className="bg-white p-6 text-center border-r-0 md:border-r-0 border-red-500 rounded-r-lg md:rounded-r-lg md:rounded-l-none">
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Öppettider</h3>
                <p className="text-gray-700 font-medium text-lg">
                  Mån-Fre: 08:00-18:00<br />
                  Lör: 10:00-15:00<br />
                  Sön: Stängt
                </p>
              </div>
            </div>

            <div className="mt-16 text-center px-4">
              <h3 className="text-xl font-medium text-gray-900 mb-4">Följ oss</h3>
              <div className="flex justify-center space-x-6">
                <a href="#" className="text-gray-400 hover:text-red-600 transition-colors">
                  <span className="sr-only">Facebook</span>
                  <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-red-600 transition-colors">
                  <span className="sr-only">Instagram</span>
                  <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.976.045-1.505.207-1.858.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.976.207 1.505.344 1.858.182.466.399.8.749 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.023.047 1.351.058 3.807.058h.468c2.456 0 2.784-.011 3.807-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.399 1.15-.749.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.047-1.023.058-1.351.058-3.807v-.468c0-2.456-.011-2.784-.058-3.807-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-96 bg-gray-100">
        <iframe 
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2034.8085329953155!2d18.0632!3d59.3293!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465f9d86d5ec1531%3A0x6c3b1d0b1c3b3b1a!2sStorgatan%2012%2C%20111%2051%20Stockholm!5e0!3m2!1ssv!2sse!4v1620000000000!5m2!1ssv!2sse" 
          width="100%" 
          height="100%" 
          style={{ border: 0 }} 
          allowFullScreen 
          loading="lazy"
          title="Karta till RenGlans kontor"
        ></iframe>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Redo att boka din städtjänst?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Fyll i vårt enkla formulär så hör vi av oss så snart som möjligt.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-red-600 hover:bg-gray-100 text-lg px-8 py-6 uppercase font-normal tracking-wide"
              onClick={() => window.location.href = '/'}
            >
              Få Gratis Offert
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              variant="outline"
              size="lg"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-red-600 text-lg px-8 py-6 uppercase font-normal tracking-wide"
            >
              Ring Oss
            </Button>
          </div>
        </div>
      </section>
      
      <Footer />
      <FloatingActionButtons />
    </div>
  );
};

export default Kontakt;
